import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import countryList from "react-select-country-list";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/checkout.css";

const CURRENCY = "EUR";
const getProductById = (id) => productData.find((product) => product.id === id);

const createOrderNumber = () => {
  const timestamp = Date.now().toString();

  if (window.crypto?.getRandomValues) {
    const randomValues = new Uint32Array(2);
    window.crypto.getRandomValues(randomValues);
    return `${timestamp}${Array.from(randomValues, (value) =>
      value.toString().padStart(10, "0")
    ).join("")}`;
  }

  return `${timestamp}${Math.floor(Math.random() * 1e12)
    .toString()
    .padStart(12, "0")}`;
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const countries = useMemo(() => countryList().getData(), []);
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    country: "",
    address: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    phone: "",
  });

  const orderItems = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: getProductById(item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const total = useMemo(
    () =>
      orderItems.reduce(
        (sum, item) =>
          sum + Number(item.product.price.replace(/,/g, "")) * item.quantity,
        0
      ),
    [orderItems]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handlePayment = (event) => {
    event.preventDefault();

    if (orderItems.length === 0) return;

    const origin = window.location.origin;
    const paymentUrl = new URL("/create_payment.php", origin);

    paymentUrl.search = new URLSearchParams({
      orderNo: createOrderNumber(),
      amount: total.toFixed(2),
      currency: CURRENCY,
      email: formData.email.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      country: formData.country,
      address: formData.address.trim(),
      addressLine2: formData.addressLine2.trim(),
      city: formData.city.trim(),
      region: formData.region.trim(),
      postalCode: formData.postalCode.trim(),
      phone: formData.phone.trim(),
      success_url: new URL("/payment?state=success", origin).toString(),
      cancel_url: new URL("/payment?state=cancel", origin).toString(),
      title: orderItems[0].product.name,
    }).toString();

    window.location.assign(paymentUrl.toString());
  };

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="checkout-page">
      <div className="checkout-layout">
        <section
          className="checkout-form-panel"
          aria-labelledby="checkout-title"
        >
          <p className="checkout-eyebrow">Secure checkout</p>
          <h1 id="checkout-title">Contact information</h1>
          <p className="checkout-intro">
            Enter your contact and delivery details to continue to our secure
            payment page.
          </p>

          {orderItems.length === 0 ? (
            <div className="checkout-empty">
              <p>Your bag is empty.</p>
              <Link to="/shop" onClick={toTop}>
                Browse jewelry
              </Link>
            </div>
          ) : (
            <form className="checkout-form" onSubmit={handlePayment}>
              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-email">Email address</label>
                <input
                  id="checkout-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
                <p className="checkout-form-note">
                  Your order confirmation will be sent to this address.
                </p>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-first-name">First name</label>
                <input
                  id="checkout-first-name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-last-name">Last name</label>
                <input
                  id="checkout-last-name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  required
                />
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  id="checkout-country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  autoComplete="country"
                  required
                >
                  <option value="" disabled>
                    Select a country or region
                  </option>
                  {countries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-address">Address</label>
                <input
                  id="checkout-address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Street address"
                  autoComplete="address-line1"
                  required
                />
              </div>

              <div className="checkout-field checkout-field--full">
                <label htmlFor="checkout-address-line-2">
                  Apartment, suite, etc. <span>(optional)</span>
                </label>
                <input
                  id="checkout-address-line-2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleInputChange}
                  autoComplete="address-line2"
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-city">City</label>
                <input
                  id="checkout-city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  autoComplete="address-level2"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-region">State / province</label>
                <input
                  id="checkout-region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  autoComplete="address-level1"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-postal-code">Postal code</label>
                <input
                  id="checkout-postal-code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  autoComplete="postal-code"
                  required
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-phone">Phone</label>
                <input
                  id="checkout-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  autoComplete="tel"
                  required
                />
              </div>

              <button type="submit" className="checkout-pay-button">
                Pay now
              </button>
            </form>
          )}

          <Link className="checkout-return-link" to="/cartpage" onClick={toTop}>
            ← Return to bag
          </Link>
        </section>

        <section className="checkout-summary" aria-labelledby="summary-title">
          <h2 id="summary-title">Order summary</h2>
          {orderItems.length === 0 ? (
            <div className="checkout-empty">
              <p>No items to display.</p>
            </div>
          ) : (
            <>
              <div className="checkout-items">
                {orderItems.map((item, index) => (
                  <article
                    key={`${item.id}-${item.size}-${item.color}-${index}`}
                    className="checkout-item"
                  >
                    <div className="checkout-item-image">
                      <img
                        src={item.product.images?.[0] || item.product.image}
                        alt={item.product.name}
                      />
                      <span>{item.quantity}</span>
                    </div>
                    <div className="checkout-item-details">
                      <h3>{item.product.name}</h3>
                      {item.size && <p>Size: {item.size}</p>}
                      {item.color && <p>Color: {item.color}</p>}
                    </div>
                    <p className="checkout-item-price">€{item.product.price}</p>
                  </article>
                ))}
              </div>
              <div className="checkout-total">
                <span>Total</span>
                <strong>
                  €{total.toFixed(2)} {CURRENCY}
                </strong>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default CheckOut;
